import {
  Button,
  Link as LinkScl,
  Modal,
  ModalCloseIconButton,
  ModalContent,
  ModalTitle,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useMutation } from '@tanstack/react-query';
import { atcb_action } from 'add-to-calendar-button-react';
import dayjs from 'dayjs';
import { useEffect, useId, useRef, useState } from 'react';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { ulid } from 'ulid';
import ErrorPage from '@/components/shared/ErrorPage';
import { constants } from '@/constants';
import useContactHashes from '@/hooks/useContactHashes';
import useResetTimeslotOnModify from '@/hooks/useResetTimeslotOnModify';
import { useAppointmentInfoQuery } from '@/features/loadAppointment/api/getAppointmentInformation';
import type { GetAppointmentInfoResponseData } from '@/features/loadAppointment/api/getAppointmentInformation';
import {
  applyAppointmentHydration,
  buildAppointmentHydrationPayload,
  isCanceledAppointment,
  shouldHydrateAppointment,
} from '@/features/loadAppointment/utils/appointmentHydration';
import {
  clearPersistedEmailAppointmentId,
  getPersistedEmailAppointmentId,
  isEmailAppointmentStateIncomplete,
  persistEmailAppointmentId,
} from '@/features/loadAppointment/utils/emailAppointmentSession';
import { queryClient } from '@/lib/queryClient';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelector,
} from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import { patchCancel } from '../api/patchCancel';
import AppointmentInformation from '../components/AppointmentInformation';
import AppointmentInformationSkeletons from '../components/AppointmentInformationSkeletons';
import CancelAppointmentModalBody from '../components/CancelAppointmentModalBody';
import useAppointmentMutationStatus from '../hooks/useAppointmentMutationStatus';
import { getEmailOption } from '../utils/getEmailOption';
import type {
  ModifyAppointmentRequestData,
  ModifyAppointmentResponseData,
} from '../utils/ModifyAppointmentRequestData';

interface EmailEvent {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  location: string;
  options: Array<
    | 'Google'
    | 'Apple'
    | 'iCal'
    | 'Microsoft365'
    | 'MicrosoftTeams'
    | 'Outlook.com'
    | 'Yahoo'
  >;
  timeZone: string;
  useUserTZ?: boolean;
}

function Confirmation() {
  const { isLoading, isError, errorSource } = useAppointmentMutationStatus();

  useBlocker(({ currentLocation, nextLocation }) => {
    const canNavigateToAgentSelection = isError && errorSource === 'reschedule';

    const allowedPaths = ['date-selection', 'cancel-appointment'];

    if (canNavigateToAgentSelection) {
      allowedPaths.push('agent-selection');
    }

    return (
      currentLocation.pathname !== nextLocation.pathname &&
      !allowedPaths.some((path) => nextLocation.pathname.includes(path))
    );
  });

  const _modalTitleId = useId();
  const _modalContentId = useId();
  const _cancellationReasonPromptId = useId();

  const [isCancellingAppointment, setIsCancellingAppointment] = useState(false);
  const hasTagged = useRef(false);

  const {
    appointmentId,
    confirmationId,
    customerId,
    contactInfo,
    selectedDestination,
    searchedDestination,
    confirmedAppointment,
    selectedBranch,
    userSearch,
    setSelectedBranch,
    setConfirmedAppointment,
    setSelectedDestination,
    setSearchedDestination,
    setSelectedAgent,
    setAppointmentId,
    setSelectedAppointmentType,
    setContactInfo,
    setSelectedStartTime,
    setSelectedEndTime,
    setConfirmationId,
    setCustomerId,
    setCancellationReason,
  } = useAppointmentStore();

  const { resetTimeslot } = useResetTimeslotOnModify();

  const { sessionId, sessionSource, flowType, setFlowType, setSessionSource, setSessionId } =
    useSessionStore();

  const { emailHash, phoneHash, contactInfoTaggingReady } = useContactHashes();

  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelector,
  );

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [appointmentCancelledFailed, setAppointmentCancelledFailed] =
    useState(false);
  const handleCloseModal = () => {
    setOpenCancelModal(false);
  };

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialEmailSourceRef = useRef(searchParams.get('source'));
  const isEmailSource = initialEmailSourceRef.current === 'email';
  const needsEmailRecovery =
    isEmailSource &&
    isEmailAppointmentStateIncomplete({ appointmentId, confirmedAppointment });
  const recoveryAppointmentId = needsEmailRecovery
    ? (getPersistedEmailAppointmentId() ?? '')
    : '';
  const recoveryHydratedRef = useRef<string | null>(null);

  const {
    data: recoveryData,
    isError: isRecoveryError,
    isLoading: isRecoveryLoading,
    refetch: refetchRecovery,
  } = useAppointmentInfoQuery(recoveryAppointmentId);

  useEffect(() => {
    if (
      !needsEmailRecovery ||
      !recoveryData ||
      !shouldHydrateAppointment(
        recoveryHydratedRef.current,
        recoveryData.appointmentId,
      )
    ) {
      return;
    }

    recoveryHydratedRef.current = recoveryData.appointmentId;

    if (isCanceledAppointment(recoveryData.status)) {
      return;
    }

    applyAppointmentHydration(buildAppointmentHydrationPayload(recoveryData), {
      setSelectedBranch,
      setConfirmedAppointment,
      setSelectedDestination,
      setSearchedDestination,
      setSelectedAgent,
      setAppointmentId,
      setSelectedAppointmentType,
      setContactInfo,
      setSelectedStartTime,
      setSelectedEndTime,
      setConfirmationId,
      setCustomerId,
      setSessionSource,
    });

    if (!sessionId) {
      setSessionId(ulid());
    }
  }, [
    needsEmailRecovery,
    recoveryData,
    setSelectedBranch,
    setConfirmedAppointment,
    setSelectedDestination,
    setSearchedDestination,
    setSelectedAgent,
    setAppointmentId,
    setSelectedAppointmentType,
    setContactInfo,
    setSelectedStartTime,
    setSelectedEndTime,
    setConfirmationId,
    setCustomerId,
    setSessionSource,
    sessionId,
    setSessionId,
  ]);

  const tagging = useTagging();

  const handleOpenCancelModal = () => {
    setOpenCancelModal(true);

    const trvEndLocation =
      searchedDestination || userSearch || selectedDestination.name;
    const commPref = isInBranchAppointment ? 'in person' : 'phone';

    tagging.tag('view', sessionSource, {
      page: Page.cancelReason,
      sessionid: sessionId,
      flow: 'cancel',
      trv_end_location: trvEndLocation,
      comm_pref: commPref,
    });
  };

  const cancelAppointment = useMutation({
    mutationFn: (cancelAppointmentData: ModifyAppointmentRequestData) =>
      patchCancel(cancelAppointmentData),
    onSuccess: (data: ModifyAppointmentResponseData) => {
      clearPersistedEmailAppointmentId();
      setIsCancellingAppointment(false);
      navigate(`/cancel-appointment/${data.appointmentId}`);
    },
    onError: (err) => {
      if (err.message !== constants.TANSTACK_ABORTED_ERROR)
        setAppointmentCancelledFailed(true);
    },
  });

  const cancelAppointmentHandler = (cancellationReason: string) => {
    if (
      initialEmailSourceRef.current === 'email' &&
      isEmailAppointmentStateIncomplete({ appointmentId, confirmedAppointment })
    ) {
      return;
    }

    setIsCancellingAppointment(true);
    setFlowType('cancel');
    setCancellationReason(cancellationReason);
    const cancelAppointmentData: ModifyAppointmentRequestData = {
      agentResourceId: confirmedAppointment?.agentInfo?.resourceId ?? '',
      appointmentId: appointmentId ?? '',
      status: 'Canceled',
      cancellationReason,
    };
    cancelAppointment.mutate(cancelAppointmentData);
  };

  const handleKeepAppointment = () => {
    if (appointmentId) {
      persistEmailAppointmentId(appointmentId);
    }
    window.location.assign(constants.TRAVEL_HOME_PAGE_URL);
  };

  useEffect(() => {
    if (flowType === 'cancel') {
      return;
    }
    if (!contactInfoTaggingReady) return;
    if (!confirmationId) return;

    if (!hasTagged.current) {
      const category = 'travel:scheduler';

      const confirmationTags = {
        category: [category],
        confirmation_id: confirmationId,
        product: ['appointment'],
        quantity: ['1'],
        custID: customerId,
      };

      // if the user lands on the confirmation page through the email link, don't fire the confirmation tags OR
      // if they end up modifying the appointment even through email, we want to fire the confirmation tags
      const shouldPopulateConfirmationTags =
        sessionSource !== 'email' ||
        (sessionSource === 'email' && flowType === 'modify');

      const trvEndLocation =
        searchedDestination || userSearch || selectedDestination.name;

      const commPref = isInBranchAppointment ? 'in person' : 'phone';

      const contactType = contactInfo.communicationPreference?.toLowerCase();

      tagging.tag('view', sessionSource, {
        page: Page.confirmation,
        sessionid: sessionId,
        flow: flowType,
        trv_end_location: trvEndLocation,
        comm_pref: commPref,
        contact_type: contactType,
        hd_schdlr_1: emailHash,
        hd_schdlr_2: phoneHash,
        // for reporting reasons, we don't want these confirmation tags to be added if the source is email
        ...(shouldPopulateConfirmationTags ? confirmationTags : {}),
      });

      hasTagged.current = true;
    }
  }, [
    confirmationId,
    contactInfo.communicationPreference,
    emailHash,
    phoneHash,
    contactInfoTaggingReady,
    customerId,
    flowType,
    searchedDestination,
    selectedDestination.name,
    sessionSource,
    sessionId,
    tagging.tag,
    userSearch,
    isInBranchAppointment,
  ]);

  const clickAddToCalendarHandler = () => {
    const selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const baseDate = dayjs(
      confirmedAppointment?.selectedDate ||
        // biome-ignore lint/style/noNonNullAssertion lint/suspicious/noNonNullAssertedOptionalChain: selectedStartTime is always set when this handler runs
        new Date(confirmedAppointment?.selectedStartTime!),
    );

    // Determine location based on appointment type
    const location =
      isInBranchAppointment && selectedBranch
        ? selectedBranch.branchAddress
        : 'Online';

    // Update description based on appointment type
    const branchName = selectedBranch?.branchName
      ? `${selectedBranch.branchName} branch`
      : 'branch';
    const destinationText = searchedDestination || selectedDestination?.name;
    const description = isInBranchAppointment
      ? `We look forward to seeing you at the ${branchName} on ${baseDate.format('MMMM D')} at ${dayjs(confirmedAppointment?.selectedStartTime).format('h:mma')} for our appointment about ${destinationText}!`
      : `We'll give you a call on ${baseDate.format('MMMM D')} at ${dayjs(confirmedAppointment?.selectedStartTime).format('h:mma')} to chat about ${destinationText}!`;

    const emailEvent: EmailEvent = {
      name: `Your appointment with ${confirmedAppointment?.agentInfo?.name}`,
      description,
      startDate: baseDate.format('YYYY-MM-DD'),
      startTime: dayjs(confirmedAppointment?.selectedStartTime).format('HH:mm'),
      endDate: dayjs(confirmedAppointment?.selectedEndTime).format(
        'YYYY-MM-DD',
      ),
      endTime: dayjs(confirmedAppointment?.selectedEndTime).format('HH:mm'),
      location,
      useUserTZ: false,
      options: [contactInfo.email ? getEmailOption(contactInfo.email) : 'iCal'],
      timeZone: selectedTimezone,
    };

    atcb_action(emailEvent);
  };

  const handleModifyAppointment = () => {
    queryClient.resetQueries({
      queryKey: ['timeSlots'],
    });

    queryClient.resetQueries({
      queryKey: ['nearbyAgents'],
    });

    resetTimeslot();
    navigate(`/reschedule-appointment/${appointmentId}/date-selection`);
  };

  if (appointmentCancelledFailed) {
    return (
      <ErrorPage
        onClickTryAgain={() => {
          window.location.replace(`${constants.APP_BASE_URL}`);
        }}
      />
    );
  }

  if (needsEmailRecovery && !recoveryAppointmentId) {
    return (
      <ErrorPage
        onClickTryAgain={() => {
          window.location.replace(`${constants.APP_BASE_URL}`);
        }}
        message='Appointment ID is missing.'
        buttonLabel='Start over'
      />
    );
  }

  if (needsEmailRecovery && isCanceledAppointment(recoveryData?.status)) {
    return (
      <ErrorPage
        onClickTryAgain={() => {
          window.location.replace(`${constants.APP_BASE_URL}`);
        }}
        message='This appointment has been cancelled.'
        imageUrl='/travel/agent/scheduler/canceledAppointmentImage.png'
        buttonLabel='Start over'
      />
    );
  }

  if (needsEmailRecovery && isRecoveryError) {
    return <ErrorPage onClickTryAgain={refetchRecovery} />;
  }

  if (isLoading || (needsEmailRecovery && isRecoveryLoading)) {
    const recoveryAppointment = recoveryData as
      | GetAppointmentInfoResponseData
      | undefined;
    const isInBranchSkeleton =
      needsEmailRecovery && isRecoveryLoading
        ? recoveryAppointment?.appointmentType === 'In Person'
        : isInBranchAppointment;

    return (
      <AppointmentInformationSkeletons isInBranch={isInBranchSkeleton} />
    );
  }

  if (isError) {
    return (
      <ErrorPage
        onClickTryAgain={() => {
          if (errorSource === 'reschedule') {
            navigate(-1);
            return;
          }

          window.location.replace(`${constants.APP_BASE_URL}`);
        }}
      />
    );
  }

  return (
    <>
      <AppointmentInformation
        onClickModifyAppointment={handleModifyAppointment}
        onClickCancelAppointment={handleOpenCancelModal}
        buttonGroup={{
          'data-testid': 'addToCalendarOrCloseButtonGroup',
          children: (
            <>
              <Button
                label='Add to calendar'
                onClick={clickAddToCalendarHandler}
                fullWidth
                variant='outlined'
                sx={{
                  backgroundColor: (theme) => theme.palette.common.white,
                }}
              />
              <LinkScl
                href={constants.TRAVEL_HOME_PAGE_URL}
                sx={{ width: '100%' }}
                data-testid='closePageButtonLink'
              >
                <Button label='Close page' fullWidth />
              </LinkScl>
            </>
          ),
        }}
      />
      <Modal
        open={openCancelModal}
        onClose={handleCloseModal}
        disableAutoFocus
        size='medium'
        sx={{
          '& .MuiPaper-root': {
            padding: 0,
          },
          '& .MuiDialog-paper': {
            minHeight: 'auto',
          },
        }}
        data-testid={'modal'}
      >
        <ModalCloseIconButton
          autoFocus
          aria-label='Close'
          onClick={handleCloseModal}
        />
        <ModalTitle
          id={_modalTitleId}
          sx={{
            p: 3,
            pb: 0,
            mb: 3,
            pr: 6,
            textAlign: 'left',
            fontWeight: 'inherit',
            fontSize: 'inherit',
            lineHeight: 'inherit',
          }}
        >
          <Typography
            component='div'
            variant='h5Medium'
            sx={{
              color: 'grey.800',
              fontWeight: 700,
              fontSize: '1.5rem',
              lineHeight: '2.25rem',
            }}
          >
            Before you cancel
          </Typography>
          <Typography
            component='div'
            variant='b1Regular'
            id={_cancellationReasonPromptId}
            sx={{
              color: 'grey.800',
              fontSize: '1rem',
              lineHeight: '1.5rem',
              fontWeight: 400,
              mt: '6px',
            }}
            data-testid='cancellationReasonPrompt'
          >
            {"Help us understand why you're canceling?"}
          </Typography>
        </ModalTitle>
        <ModalContent id={_modalContentId} contentFadeVariant='none'>
          <CancelAppointmentModalBody
            cancellationReasonPromptId={_cancellationReasonPromptId}
            isModalOpen={openCancelModal}
            onClickKeepAppointment={handleKeepAppointment}
            onClickCancel={cancelAppointmentHandler}
            isCancellingAppointment={isCancellingAppointment}
          />
        </ModalContent>
      </Modal>
    </>
  );
}

export default Confirmation;
